import type { ChatHistory } from "@/lib/types";

/** Hidden instructions marker for prompt augmentation */
export const HIDDEN_PROMPT_SUFFIX_MARKER =
  "\n\nFormat all responses in markdown format (do not include three backticks followed by 'markdown' at the start of the response).\n\nHere is the context of the conversation:\n\n";

/** Strip the hidden prompt-augmentation suffix from a stored question */
export function stripHiddenPromptSuffix(storedQuestion: string): string {
  const idx = storedQuestion.indexOf(HIDDEN_PROMPT_SUFFIX_MARKER);
  return idx >= 0 ? storedQuestion.slice(0, idx) : storedQuestion;
}

/** Display name for a past chat: the user-facing title, or the trimmed first
 * question as the fallback for chats saved before titles existed. */
export function chatTitleOf(item: { title?: string; question: string }): string {
  const title = item.title?.trim();
  if (title) return title;
  const question = stripHiddenPromptSuffix(item.question).trim();
  return question.length > 50 ? `${question.slice(0, 49)}…` : question;
}

/** ChatGPT-style date bucket label for a stored turn timestamp. */
export function historyDateBucket(iso: string | undefined): string {
  if (!iso) return "Older";
  try {
    const d = new Date(iso);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const daysAgo = Math.floor(
      (startOfToday.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / dayMs
    );
    if (daysAgo <= 0) return "Today";
    if (daysAgo === 1) return "Yesterday";
    if (daysAgo < 7) return "Previous 7 days";
    return "Older";
  } catch {
    return "Older";
  }
}

/** Group chat history items into ChatGPT-style date sections. */
export function groupChatsByDate(
  items: ChatHistory[]
): Array<{ label: string; items: ChatHistory[] }> {
  const order = ["Pinned", "Today", "Yesterday", "Previous 7 days", "Older"];
  const groups = new Map<string, ChatHistory[]>();
  for (const item of items) {
    const label = item.is_pinned ? "Pinned" : historyDateBucket(item.created_at);
    const bucket = groups.get(label);
    if (bucket) bucket.push(item);
    else groups.set(label, [item]);
  }
  return Array.from(groups.entries())
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([label, groupItems]) => ({ label, items: groupItems }));
}

/** Format ISO timestamp to relative date label */
export function formatRelativeDate(iso: string | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const daysAgo = Math.floor(
      (startOfToday.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / dayMs
    );
    if (daysAgo <= 0) return `Today ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    if (daysAgo === 1) return "Yesterday";
    if (daysAgo < 7) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

/** Format an ISO timestamp string to a readable time label */
export function formatTime(iso: string | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}
